/**
 * Notes: the workspace's own knowledge.
 *
 * One function per route in `apps/handbook-mcp/app/api/v1/notes.py`. No function takes a
 * workspace — there is no parameter for one, in the path, the query or the body. The workspace
 * comes from the credential and only from the credential.
 */

import { authed } from "./authed";
import type { Note, NoteCategory, NoteGrade } from "./types";

export interface ListNotesQuery {
  /** `draft` or `published`. The derived states — edited, flagged — are filtered client-side. */
  status?: "draft" | "published";
  /** Full-text over this workspace's own titles and bodies. The API requires at least 2 characters. */
  q?: string;
  limit?: number;
  offset?: number;
}

/** `Query(default=50, ge=1, le=200)` on `list_notes`. Asking for more is a 422, not a bigger page. */
const MAX_PAGE = 200;

/**
 * A ceiling on the paging loop below, so a server that keeps answering full pages cannot spin this
 * forever. `quota_notes_per_workspace` defaults to 250, so two pages is the realistic case and
 * five is already generous.
 */
const MAX_PAGES = 5;

export function listNotes(query: ListNotesQuery = {}, signal?: AbortSignal): Promise<Note[]> {
  const params = new URLSearchParams();
  if (query.status) params.set("status", query.status);
  if (query.q) params.set("q", query.q);
  if (query.limit !== undefined) params.set("limit", String(query.limit));
  if (query.offset !== undefined) params.set("offset", String(query.offset));
  const suffix = params.toString();
  return authed<Note[]>(`/notes${suffix ? `?${suffix}` : ""}`, { signal });
}

/** What a full read of the workspace's notes came back with, and whether it was actually full. */
export interface AllNotes {
  notes: Note[];
  /**
   * False when the paging ceiling was reached before the server ran out of rows. A count taken
   * from a partial read is a count that is wrong, and the sidebar would rather show nothing than
   * a number that is quietly too small — so it reads this rather than `notes.length` alone.
   */
  complete: boolean;
}

/**
 * Every note in the workspace, paged until the server runs out.
 *
 * Two screens need the whole set rather than a page. The sidebar counts notes per category, and
 * the category filter is applied here because `GET /notes` has no `category` parameter — filtering
 * one page of fifty and calling the result "Rules" would silently hide the fifty-first.
 *
 * `offset` paging over rows ordered by `updated_at` can in principle repeat or skip a row if
 * something is edited mid-read. That is why each page is keyed by id when it is merged: the worst
 * a concurrent edit can do here is cost a note from the count, never duplicate one into it.
 */
export async function listAllNotes(
  query: Omit<ListNotesQuery, "limit" | "offset"> = {},
  signal?: AbortSignal,
): Promise<AllNotes> {
  const seen = new Map<string, Note>();
  for (let page = 0; page < MAX_PAGES; page += 1) {
    const batch = await listNotes(
      { ...query, limit: MAX_PAGE, offset: page * MAX_PAGE },
      signal,
    );
    for (const note of batch) seen.set(note.id, note);
    if (batch.length < MAX_PAGE) return { notes: [...seen.values()], complete: true };
  }
  return { notes: [...seen.values()], complete: false };
}

export function getNote(id: string, signal?: AbortSignal): Promise<Note> {
  return authed<Note>(`/notes/${id}`, { signal });
}

/**
 * Always created as a draft. Publishing is always a deliberate second act.
 *
 * `category` defaults to `knowledge` server-side. It is sent explicitly when the person created
 * the note from inside a category, so a draft started under "Rules" is a rule.
 */
export function createNote(input: {
  title: string;
  body?: string;
  tags?: string[];
  category?: NoteCategory;
  grade?: NoteGrade;
}): Promise<Note> {
  return authed<Note>("/notes", { method: "POST", body: input });
}

/**
 * Content only. A published note that is edited stays published — the *older* version is what an
 * agent keeps being served until publish is pressed again, which is the thing the editor has to
 * say out loud.
 */
export function updateNote(
  id: string,
  patch: {
    title?: string;
    body?: string;
    tags?: string[];
    category?: NoteCategory;
    /**
     * **There is no way to remove a grade, only to change it.** `NoteUpdate` treats an absent
     * field as unchanged, and `NoteRepository.update_fields` writes `grade` only when it is not
     * `None` — so sending `null` is indistinguishable from sending nothing. A grade does get
     * cleared, but by the *category*: moving a note off `rule` sets `grade` to `NULL` server-side,
     * which is why this app never has to send a clearing value. The editor says so on the screen
     * rather than offering a "no grade" option that would quietly do nothing.
     */
    grade?: NoteGrade;
  },
  signal?: AbortSignal,
): Promise<Note> {
  return authed<Note>(`/notes/${id}`, { method: "PATCH", body: patch, signal });
}

/** The separate, explicit act. Publishing is what exposes a note to an agent. */
export function publishNote(id: string): Promise<Note> {
  return authed<Note>(`/notes/${id}/publish`, { method: "POST" });
}

export function deleteNote(id: string): Promise<void> {
  return authed<void>(`/notes/${id}`, { method: "DELETE" });
}
